import React, { useEffect, useRef, useState } from 'react';
import AutoSuggest from 'react-autosuggest';
import { useFormikContext } from 'formik';
import { COUNTRY, API } from '../../Constants';

const PeliasAutocomplete = (props) => {
    const { setFieldValue, errors } = useFormikContext();
    const [value, setValue] = useState(props.inputProps.value || '');
    const [suggestions, setSuggestions] = useState([]);
    const debounceTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const lastQueryRef = useRef('');

    function cancelActiveRequest() {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }

    function getSuggestions(nextValue) {
        const query = (nextValue || '').trim();
        lastQueryRef.current = query;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        if (query.length < 3) {
            cancelActiveRequest();
            setSuggestions([]);
            return;
        }

        debounceTimerRef.current = setTimeout(async () => {
            cancelActiveRequest();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await fetch(
                    `${API.AUTOCOMPLETE_URL}?boundary.country=${COUNTRY}&layers=address&text=${encodeURIComponent(query)}`,
                    { signal: controller.signal }
                );
                const data = await response.json();

                if (lastQueryRef.current === query) {
                    setSuggestions(data.features || []);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.warn('pelias autocomplete error', error);
                    setSuggestions([]);
                }
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
            }
        }, 300);
    }

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            cancelActiveRequest();
        };
    }, []);

    const theme = {
        container: 'autosuggest',
        input: `${!!errors.addressLine1 ? 'form-control is-invalid' : 'form-control'}`,
        suggestionsContainer: 'dropdown',
        suggestionsList: `dropdown-menu w-100 ${suggestions.length ? 'show' : ''}`,
        suggestion: 'dropdown-item text-truncate',
        suggestionHighlighted: 'active',
    };

    return (
        <>
            <AutoSuggest
                suggestions={suggestions}
                onSuggestionsClearRequested={() => setSuggestions([])}
                onSuggestionsFetchRequested={({ value }) => {
                    setValue(value);
                    getSuggestions(value);
                }}
                onSuggestionSelected={(_, { suggestion }) => {
                    setFieldValue('latitude', suggestion.geometry.coordinates[1]);
                    setFieldValue('longitude', suggestion.geometry.coordinates[0]);
                    setFieldValue('addressLine1', suggestion.properties.name);
                    setFieldValue('city', suggestion.properties.locality);
                    setFieldValue('state', suggestion.properties.region);
                    if (suggestion.properties.county !== undefined)
                        setFieldValue('county', suggestion.properties.county);
                    if (suggestion.properties.postalcode !== undefined)
                        setFieldValue('zip', suggestion.properties.postalcode);
                }}
                getSuggestionValue={(suggestion) => suggestion.properties.name}
                renderSuggestion={(suggestion) => {
                    const {
                        properties: { name, locality, region },
                    } = suggestion;
                    const text = [name, locality, region].filter(Boolean).join(', ');
                    return (
                        <span key={suggestion.properties.id} title={text}>
                            {text}
                        </span>
                    );
                }}
                inputProps={{
                    ...props.inputProps,
                    value: value,
                    onChange: (_, { newValue, method }) => {
                        setValue(newValue);
                        setFieldValue('addressLine1', newValue);
                    },
                }}
                // highlightFirstSuggestion={true}
                theme={theme}
            />
        </>
    );
};

export default PeliasAutocomplete;
